import React, { useState, useEffect } from "react";
import { Loader } from "../Loader";
import { Button } from "../Button";
import {
  getPushNotificationStatsForAdmin,
  PushNotificationStatsViewModel,
  getFirebasePushCampaignStatsForAdmin,
  getPushCampaignPartnerImpactForAdmin,
  PushCampaignStatRow,
  PushCampaignPartnerImpactRow,
  PlatformSplit,
  PlatformRate,
} from "../../api/communications";

const toISOStart = (date: string) => `${date}T00:00:00+00:00`;
const toISOEnd = (date: string) => `${date}T23:59:59+00:00`;

const n = (val: number | null | undefined) => val ?? 0;
const pct = (val: number | null | undefined) => `${n(val).toFixed(2)}%`;

// A campaign name is not unique — Firebase lists one campaign per send and fourteen names have been
// reused. The send date is what tells two of them apart.
const sendKey = (row: { campaignName: string; sendDate: string }) =>
  `${row.campaignName}|${row.sendDate}`;

const kr = (val: number | null | undefined) =>
  `${n(val).toLocaleString(undefined, { maximumFractionDigits: 0 })} kr.`;

// The backend sends instants, but every other date on this row is Copenhagen local — showing the
// window in UTC made a midnight start read as 22:00 and contradict its own badge.
const shortDateTime = (iso: string) =>
  new Date(iso).toLocaleString("sv-SE", {
    timeZone: "Europe/Copenhagen",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

// A campaign only has an open rate if it has a denominator — a null rate means "unknown", not
// "nobody opened it", and averaging those in would drag the mean down with a number we never
// measured. The legacy fields keep older payloads working while a deploy is mixed.
const withOpenRate = (campaigns: PushCampaignStatRow[]) =>
  campaigns.filter(
    (campaign) =>
      campaign.openRates?.total != null || n(campaign.impressions) > 0 || n(campaign.sends) > 0
  );

const campaignRate = (campaign: PushCampaignStatRow) =>
  campaign.openRates?.total?.value ?? n(campaign.openRate);

const platformSplit = (ios: number, android: number, web?: number) => (
  <>
    <div>
      <span className="text-gray-400">iOS</span>{" "}
      <span className="font-semibold">{ios.toLocaleString()}</span>
    </div>
    <div>
      <span className="text-gray-400">Android</span>{" "}
      <span className="font-semibold">{android.toLocaleString()}</span>
    </div>
    {web != null && (
      <div>
        <span className="text-gray-400">Web</span>{" "}
        <span className="font-semibold">{web.toLocaleString()}</span>
      </div>
    )}
  </>
);

const STAT_CARDS: {
  label: string;
  value: (
    stats: PushNotificationStatsViewModel | null,
    campaigns: PushCampaignStatRow[] | null
  ) => string;
  detail: (
    stats: PushNotificationStatsViewModel | null,
    campaigns: PushCampaignStatRow[] | null
  ) => React.ReactNode;
}[] = [
  {
    label: "Opens",
    value: (s) => (s ? n(s.totalOpens).toLocaleString() : "-"),
    detail: (s) => (s ? platformSplit(n(s.iosOpens), n(s.androidOpens), n(s.webOpens)) : null),
  },
  {
    label: "Foreground",
    value: (s) => (s ? n(s.totalForeground).toLocaleString() : "-"),
    detail: (s) => (s ? platformSplit(n(s.iosForeground), n(s.androidForeground), n(s.webForeground)) : null),
  },
  {
    // Averaged over the campaigns listed below rather than pooled, so a single large campaign
    // cannot mask how the rest performed.
    label: "Average Open Rate",
    value: (_stats, campaigns) => {
      const eligible = campaigns ? withOpenRate(campaigns) : [];

      return eligible.length > 0
        ? pct(eligible.reduce((sum, campaign) => sum + campaignRate(campaign), 0) / eligible.length)
        : "-";
    },
    detail: (_stats, campaigns) => {
      const eligible = campaigns ? withOpenRate(campaigns) : [];

      return eligible.length > 0 ? (
        <div className="text-gray-400">Mean of {eligible.length} campaigns</div>
      ) : null;
    },
  },
];

// The exact figure, the ≈ estimate when that is all there is, or "n/a" where the platform was
// never measured — never a zero that would read as "nobody".
const breakdownValue = (exact: number | null | undefined, estimate?: number | null): React.ReactNode =>
  exact != null ? (
    <span className="font-semibold text-gray-700">{exact.toLocaleString()}</span>
  ) : estimate != null ? (
    <span className="font-semibold text-gray-500">≈ {estimate.toLocaleString()}</span>
  ) : (
    <span className="text-gray-300">n/a</span>
  );

/** The Firebase-style fold-out: one row per platform, only shown when the card is expanded. */
const PlatformBreakdown: React.FC<{ campaign: PushCampaignStatRow }> = ({ campaign }) => {
  const platforms = [
    { name: "iOS", key: "ios" as const },
    { name: "Android", key: "android" as const },
    { name: "Web", key: "web" as const },
  ];

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 overflow-x-auto">
      <table className="text-sm min-w-[560px]">
        <thead>
          <tr className="text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">
            <th className="pr-6 pb-1 font-medium"></th>
            <th className="pr-6 pb-1 font-medium">Sent</th>
            <th className="pr-6 pb-1 font-medium">Received</th>
            <th className="pr-6 pb-1 font-medium">Opened</th>
            <th className="pr-6 pb-1 font-medium">Foreground</th>
            <th className="pr-6 pb-1 font-medium">Dismissed</th>
            <th className="pb-1 font-medium">Open rate</th>
          </tr>
        </thead>
        <tbody>
          {platforms.map(({ name, key }) => (
            <tr key={key} className="align-top">
              <td className="pr-6 py-0.5 text-gray-400">{name}</td>
              <td className="pr-6 py-0.5">{breakdownValue(campaign.sent?.[key], campaign.sentEstimated?.[key])}</td>
              <td className="pr-6 py-0.5">{breakdownValue(campaign.received?.[key])}</td>
              <td className="pr-6 py-0.5">{breakdownValue(campaign.opened?.[key])}</td>
              <td className="pr-6 py-0.5">{breakdownValue(campaign.openedForeground?.[key])}</td>
              <td className="pr-6 py-0.5">
                {breakdownValue(campaign.dismissed?.[key] ?? campaign.dismissedApproximate?.[key])}
              </td>
              <td className="py-0.5">
                {rateText(campaign.openRates?.[key]) ?? <span className="text-gray-300">n/a</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const rateText = (rate: PlatformRate | null | undefined) =>
  rate == null ? null : `${rate.isApproximate ? "≈ " : ""}${rate.value.toFixed(2)}%`;

// The Sent headline: the exact figure once the send's bulks were identified, the console's own
// bucket where we imported it, the frozen estimate's bucket meanwhile — and "-" before
// measurement. The headline speaks in the console's buckets so an estimate never reads more
// precise than the console it approximates; the fold-out carries the per-platform numbers.
const sentTotal = (c: PushCampaignStatRow) => {
  if (c.sent?.total != null) return c.sent.total.toLocaleString();
  if (c.consoleSendsRange) return c.consoleSendsRange;
  if (c.estimatedSendsRange) return `≈ ${c.estimatedSendsRange}`;
  if (c.sentEstimated?.total != null) return `≈ ${c.sentEstimated.total.toLocaleString()}`;
  return "-";
};

const totalOf = (split: PlatformSplit | undefined, fallback: number) =>
  (split?.total ?? fallback).toLocaleString();

// Dismissals a platform could only place by the day they happened join the total. The footnote
// carries the caveat; the cell itself stays a plain number.
const dismissedTotal = (c: PushCampaignStatRow) => {
  const approximate = c.dismissedApproximate?.total;

  return approximate != null
    ? ((c.dismissed?.total ?? 0) + approximate).toLocaleString()
    : totalOf(c.dismissed, n(c.dismisses));
};

// Sales sit in the same row as the campaign's own numbers rather than in a block of their own:
// they answer a question about the campaign, and reading them meant looking somewhere else.
const SALES_STATS: {
  label: string;
  value: (impact: PushCampaignPartnerImpactRow) => string;
  color: string;
}[] = [
  { label: "Sales",        value: (i) => n(i.salesCount).toLocaleString(), color: "text-green-600" },
  { label: "Sales amount", value: (i) => kr(i.salesAmount),                color: "text-green-600" },
];

const Badge: React.FC<{ tone: "amber" | "gray"; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className={`text-xs px-2 py-0.5 rounded-full ${
      tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {children}
  </span>
);

const StatCell: React.FC<{ label: string; value: React.ReactNode; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div>
    <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
  </div>
);

/**
 * Which partner the campaign promoted, read off its own deeplink by the nightly sync.
 *
 * Rendered only when there is one. A push promoting a game, a section or generic content carries no
 * such link and simply has nothing to say here.
 */
const PartnerName: React.FC<{ impact: PushCampaignPartnerImpactRow }> = ({ impact }) => (
  <>
    <Badge tone="gray">{impact.businessName?.trim() || impact.businessContentfulId}</Badge>
    {impact.salesTracked ? (
      <>
        <span className="text-xs text-gray-400">
          sales {shortDateTime(impact.windowStart)} → {shortDateTime(impact.windowEnd)}
        </span>
        {!impact.windowComplete && <Badge tone="amber">Window still open</Badge>}
      </>
    ) : (
      // No sale of this partner ever reaches us (an app-install push, OiSTER's own product) —
      // a 0 here would read as "the campaign failed", so the sales cells stay off entirely.
      <span className="text-xs text-gray-300">sales not tracked</span>
    )}
  </>
);

/**
 * The sales columns appear only for a campaign that promoted a partner.
 *
 * Most campaigns promote a game, a section or generic content and have no partner to sell for, so
 * carrying two permanently empty columns across every row would cost more attention than it repays.
 * A campaign that does have the numbers gains the columns, and their presence is the signal.
 */
const PushCampaignRow: React.FC<{
  campaign: PushCampaignStatRow;
  impact: PushCampaignPartnerImpactRow | undefined;
}> = ({ campaign, impact }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="w-full text-left"
      >
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="font-medium text-gray-800">{campaign.campaignName}</span>
          <span className="text-sm text-gray-500">{campaign.sendDate}</span>
          {campaign.analyticsLabel && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {campaign.analyticsLabel}
            </span>
          )}
          {impact && <PartnerName impact={impact} />}
          <span className="ml-auto text-gray-400 text-xs flex items-center gap-1">
            platforms
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
              viewBox="0 0 20 20" fill="currentColor" aria-hidden
            >
              <path fillRule="evenodd" clipRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
            </svg>
          </span>
        </div>

        <div
          className={`grid grid-cols-3 sm:grid-cols-4 gap-3 ${
            impact?.salesTracked ? "lg:grid-cols-7" : "lg:grid-cols-5"
          }`}
        >
          <StatCell label="Sent" value={sentTotal(campaign)} color="text-gray-800" />
          <StatCell
            label="Received"
            value={campaign.received?.total != null ? campaign.received.total.toLocaleString() : "-"}
            color="text-gray-800"
          />
          <StatCell
            label="Opened"
            value={totalOf(campaign.opened, n(campaign.opens) + n(campaign.foreground))}
            color="text-green-600"
          />
          <StatCell
            label="Dismissed"
            value={dismissedTotal(campaign)}
            color="text-orange-500"
          />
          <StatCell
            label="Open Rate"
            value={rateText(campaign.openRates?.total) ?? (campaignRate(campaign) > 0 ? pct(campaignRate(campaign)) : "-")}
            color="text-green-600"
          />
          {impact?.salesTracked &&
            SALES_STATS.map((stat) => (
              <StatCell key={stat.label} label={stat.label} value={stat.value(impact)} color={stat.color} />
            ))}
        </div>
      </button>

      {expanded && <PlatformBreakdown campaign={campaign} />}
    </div>
  );
};

const PushNotificationsTab: React.FC = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // Server-side: the list arrives aggregated, so slicing to one platform must happen in the query.
  const [platform, setPlatform] = useState("");
  // Client-side: the whole list is already loaded, so these narrow it instantly.
  const [nameFilter, setNameFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");

  const [pushStats, setPushStats] = useState<PushNotificationStatsViewModel | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [campaigns, setCampaigns] = useState<PushCampaignStatRow[] | null>(null);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);

  const [impact, setImpact] = useState<Record<string, PushCampaignPartnerImpactRow>>({});
  const [impactError, setImpactError] = useState<string | null>(null);


  const dateParams = () => ({
    from: from ? toISOStart(from) : undefined,
    to: to ? toISOEnd(to) : undefined,
  });

  const fetchData = async () => {
    const params = dateParams();

    setStatsLoading(true);
    setCampaignsLoading(true);

    // Kept apart on purpose: the impact call reads the transaction tables, so the campaign list
    // must not wait on it — and a failure there degrades to its own banner. Rows render without
    // sales columns until it lands, then gain them.
    const [statsResult, campaignsResult, impactResult] = await Promise.allSettled([
      getPushNotificationStatsForAdmin(params),
      getFirebasePushCampaignStatsForAdmin({ ...params, platform: platform || undefined }),
      getPushCampaignPartnerImpactForAdmin(params),
    ]);

    if (statsResult.status === "fulfilled") {
      setPushStats(statsResult.value);
      setStatsError(null);
    } else {
      setStatsError("Failed to fetch push open statistics");
    }
    setStatsLoading(false);

    if (campaignsResult.status === "fulfilled") {
      setCampaigns(campaignsResult.value);
      setCampaignsError(null);
    } else {
      setCampaignsError("Failed to fetch push campaign stats");
    }
    setCampaignsLoading(false);

    if (impactResult.status === "fulfilled") {
      setImpact(Object.fromEntries(impactResult.value.map((row) => [sendKey(row), row])));
      setImpactError(null);
    } else {
      setImpactError("Failed to fetch partner clicks and sales");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partnerOptions = Array.from(
    new Set((campaigns ?? []).map((c) => c.linkedBusinessName?.trim()).filter((name): name is string => !!name))
  ).sort((a, b) => a.localeCompare(b));

  const visibleCampaigns = (campaigns ?? []).filter(
    (c) =>
      (!nameFilter || c.campaignName.toLowerCase().includes(nameFilter.trim().toLowerCase())) &&
      (!partnerFilter || c.linkedBusinessName?.trim() === partnerFilter)
  );

  return (
    <div>
      {/* Date range */}
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="p-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="p-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="p-2 border border-gray-300 rounded-md bg-white"
          >
            <option value="">All platforms</option>
            <option value="IOS">iOS</option>
            <option value="ANDROID">Android</option>
            <option value="WEB">Web</option>
          </select>
        </div>
        <Button onClick={fetchData}>Apply</Button>
      </div>

      {/* Overall GA4 open statistics */}
      <section className="mb-12">
        <h3 className="text-lg font-medium mb-4">Push open statistics (GA4)</h3>
        {statsError && <div className="error mb-4">{statsError}</div>}
        {/* The average open rate card reads the campaign list, so wait for both. */}
        {statsLoading || campaignsLoading ? (
          <div className="flex justify-center my-8">
            <Loader />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {STAT_CARDS.map((card) => (
              <div key={card.label} className="bg-white rounded-lg shadow p-4">
                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-1">
                  {card.label}
                </div>
                <div className="text-3xl font-bold text-gray-800">
                  {card.value(pushStats, campaigns)}
                </div>
                <div className="mt-3 flex gap-6 text-sm">{card.detail(pushStats, campaigns)}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Per-campaign stats (Firebase, via BigQuery export) */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h3 className="text-lg font-medium">Per campaign (Firebase)</h3>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="search"
              placeholder="Search campaign…"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded-md w-56"
            />
            <select
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded-md bg-white"
            >
              <option value="">All partners</option>
              {partnerOptions.map((partner) => (
                <option key={partner} value={partner}>{partner}</option>
              ))}
            </select>
          </div>
        </div>
        {campaignsError && <div className="error mb-4">{campaignsError}</div>}
        {impactError && <div className="error mb-4">{impactError}</div>}
        {campaignsLoading ? (
          <div className="flex justify-center my-8">
            <Loader />
          </div>
        ) : visibleCampaigns.length > 0 ? (
          visibleCampaigns.map((campaign) => (
            <PushCampaignRow
              key={sendKey(campaign)}
              campaign={campaign}
              impact={impact[sendKey(campaign)]}
            />
          ))
        ) : campaigns && campaigns.length > 0 ? (
          <div className="text-center text-gray-500 my-8">
            No campaigns match the current filters.
          </div>
        ) : (
          <div className="text-center text-gray-500 my-8">
            No campaign stats yet. Per-campaign data appears once the Firebase → BigQuery export is
            enabled and the nightly sync has run.
          </div>
        )}
        <p className="text-xs text-gray-400 mt-2">
          Sent is how many messages FCM accepted — the console's own Sends — split by platform from
          the FCM export; ≈ marks the calibrated device-count estimate used until an exact figure
          exists, and n/a means the platform was never measured. Received counts notifications that
          reached a device: from the delivery export where the app version reports it, otherwise
          from Android's own receipts. Opened includes notifications that arrived with the app open
          (foreground) — in the count and in the open-rate denominator. Open rate is opened ÷
          received; where received isn't measured yet it falls back to opened ÷ sent, with ≈ when
          that sent is an estimate. iOS dismissals arrive without a campaign name, so a send that
          was its day's only campaign is credited with the day's — which counts that day's
          transactional pushes too; web received arrives with the site's next release.
          Campaigns before late July 2026 have no platform breakdown — totals only. Average Open
          Rate is the mean of these campaigns' own rates and skips campaigns with no denominator.
        </p>
        <p className="text-xs text-gray-400 mt-2">
          The promoted partner is read off the campaign's own deeplink, so a push that promoted a
          game, a section or generic content shows <strong>n/a</strong> rather than being attributed
          to whoever happened to be busy that hour. Sales cover the 96 hours after the send and count{" "}
          <strong>every</strong> purchase from that partner in the window, whether or not the buyer
          arrived from the push. There is no control group, so read these as what happened alongside
          a campaign, not as what it caused.
        </p>
      </section>
    </div>
  );
};

export default PushNotificationsTab;
